import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  Grid,
  Divider,
  Button,
  CircularProgress,
  Checkbox,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { dashboardService } from "../../api/dashboardService";

import logo from "../../assets/images/logo.png";

import dogPlaceholder from "../../assets/images/dogPlaceholder.png";
import userPlaceholder from "../../assets/images/userPlaceholder.png";

const MemberCard = ({ member, teamType, selectable, selected, disabled, onSelect }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const fallbackImage =
    teamType === "teamPaws" ? dogPlaceholder : userPlaceholder;

  const getImageUrl = (value) => {
    const images = Array.isArray(value) ? value : [value];
    return images.find((image) => typeof image === "string" && image.trim())?.trim();
  };
  const profileImage = getImageUrl(member.user_profile_image) ||
    (teamType === "teamPaws" && getImageUrl(member.dog_images)) ||
    fallbackImage;

  const bioText = member.user_bio || "";
  const isLongBio = bioText.length > 80;

  const memberName = member.user_name || "Member";
  const locationLabel = [member.neighborhood, member.user_city, member.user_state]
    .filter(Boolean).join(", ");

  return (
    <Card
      sx={{
        position: "relative", height: "100%", display: "flex", flexDirection: "column",
        p: 2, borderRadius: "24px", bgcolor: "#fff", overflow: "hidden",
        border: selected ? "2px solid #00bd8b" : "2px solid transparent",
        boxShadow: selected ? "0 0 0 3px rgba(0,189,139,0.12)" : "0 4px 20px rgba(0,0,0,0.05)",
      }}
    >
      <Box sx={{ position: "relative" }}>
        <Box
          component="img"
          src={profileImage}
          alt={memberName}
          loading="lazy"
          onError={(event) => {
            event.currentTarget.onerror = null;
            if (event.currentTarget.getAttribute("src") !== fallbackImage) {
              event.currentTarget.src = fallbackImage;
            }
          }}
          sx={{ display: "block", width: "100%", aspectRatio: "1 / 1", objectFit: profileImage === fallbackImage ? "contain" : "cover", bgcolor: "#f3f6f5", border: "3px solid #f4df55" }}
        />
        {selectable && (
          <Checkbox
            checked={selected}
            disabled={disabled}
            onChange={onSelect}
            inputProps={{ "aria-label": `Select ${member.user_name || member.user_email || member.id}` }}
            sx={{ position: "absolute", top: 10, right: 10, bgcolor: "rgba(255,255,255,0.95)", borderRadius: "50%", color: "#53636a", "&.Mui-checked": { color: "#00a77c" }, "&:hover": { bgcolor: "#fff" } }}
          />
        )}
      </Box>
      <Box sx={{ px: 1, pt: 2.5, pb: 1, display: "flex", flexDirection: "column", flexGrow: 1, gap: 1.25 }}>
        <Typography variant="h6" sx={{ color: "#45525c", fontWeight: 700, lineHeight: 1.3, overflowWrap: "anywhere" }}>
          {memberName}
        </Typography>
        <Typography variant="body2" sx={{ color: "#77838b", overflowWrap: "anywhere" }}>
          {member.user_email}
        </Typography>
        {locationLabel && (
          <Typography variant="body2" sx={{ alignSelf: "flex-start", px: 1.5, py: 0.4, borderRadius: "20px", bgcolor: "#f5f8fa", border: "1px solid #e2e8eb", color: "#53636a" }}>
            📍 {locationLabel}
          </Typography>
        )}
        <Typography variant="body2" sx={{ alignSelf: "flex-start", px: 1.5, py: 0.4, borderRadius: "20px", bgcolor: "#e4f6ef", border: "1px solid #cce8df", color: "#43655a" }}>
          {teamType === "teamPaws" ? "Pup Parent" : "Pup Pal"}
        </Typography>
        {bioText && (
          <Box>
            <Typography variant="body2" color="text.secondary">
              {isExpanded || !isLongBio ? bioText : `${bioText.substring(0, 80)}…`}
            </Typography>
            {isLongBio && (
              <Button size="small" onClick={() => setIsExpanded(!isExpanded)} sx={{ p: 0, textTransform: "none" }}>
                {isExpanded ? "Show Less" : "Read More"}
              </Button>
            )}
          </Box>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          component="a"
          href={`https://webapp.socialpawsapp.com/profile-details?profileId=${encodeURIComponent(member.id)}`}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          disableElevation
          sx={{ mt: 2, py: 1.5, borderRadius: "32px", bgcolor: "#00bf8f", fontWeight: 700, fontSize: "1rem", textTransform: "none", "&:hover": { bgcolor: "#00a77c" } }}
        >
          View profile
        </Button>
      </Box>
    </Card>
  );
};

const NewJoinMembers = ({ selectionMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const filters = location.state || {
    timeFilter: "today",
    startDate: "",
    endDate: "",
  };

  const today = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Colombo", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
  const { timeFilter, startDate, endDate } = selectionMode
    ? { timeFilter: "custom", startDate: "2026-01-01", endDate: today }
    : filters;
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [previewPage, setPreviewPage] = useState(0);
  const [isEmailPreviewOpen, setIsEmailPreviewOpen] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ type: "", msg: "" });
  const [nameSearch, setNameSearch] = useState({ teamPaws: "", teamSocial: "" });
  const [expandedTeams, setExpandedTeams] = useState({ teamPaws: true, teamSocial: true });

  const toggleMember = (member, teamType) => {
    if (isSendingEmail) return;
    setEmailStatus({ type: "", msg: "" });
    setSelectedMembers((previous) => {
      if (previous.some((selected) => selected.id === member.id)) {
        return previous.filter((selected) => selected.id !== member.id);
      }
      return [...previous, { ...member, teamType }];
    });
  };

  const handleSendEmail = async () => {
    if (!isEmailPreviewOpen || selectedMembers.length < 2 || isSendingEmail) return;
    setIsSendingEmail(true);
    setEmailStatus({ type: "", msg: "" });
    try {
      const response = await dashboardService.sendDatingEmail(
        selectedMembers.map((member) => member.id),
      );
      if (response.data?.success === false || response.data?.status === false ||
          response.data?.data?.success === false) {
        throw new Error("Email request failed.");
      }
      if (Number(response.data?.data?.failed) > 0) {
        setEmailStatus({ type: "warning", msg: "Some emails failed. Check delivery before sending again." });
      } else {
        setEmailStatus({ type: "success", msg: "Email request submitted successfully!" });
      }
      setSelectedMembers([]);
      setIsEmailPreviewOpen(false);
    } catch (err) {
      setEmailStatus({ type: "error", msg: "Email sending could not be confirmed. Check delivery before trying again." });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const getDisplayTitle = () => {
    const titles = {
      today: "Today",
      past7Days: "Past 7 Days",
      past30Days: "Past 30 Days",
      total: "Total",
      custom: "Custom Range",
    };
    return titles[timeFilter] || "Today";
  };

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setLoading(true);
        setError("");
        setSelectedMembers([]);
        setEmailStatus({ type: "", msg: "" });
        const adminId = sessionStorage.getItem("admin_id") || 1;

        const response = await dashboardService.getUserCounts(
          adminId,
          timeFilter,
          startDate,
          endDate,
        );

        if (response?.data?.status) {
          setUsers(response.data.data.users);
        } else {
          setError("Failed to load members.");
        }
      } catch (err) {
        console.error("Error fetching new join members:", err);
        setError("An error occurred while connecting to the server.");
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [timeFilter, startDate, endDate]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !users) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="h6" mb={2} color="error">
          {error || "No user data found. Please return to the dashboard."}
        </Typography>
        <Button variant="contained" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  const teamPaws = users.teamPaws || [];
  const teamSocial = users.teamSocial || [];
  const filterByName = (members, teamType) => members.filter((member) =>
    (member.user_name || "").toLowerCase().includes(nameSearch[teamType].trim().toLowerCase()),
  );
  const filteredTeamPaws = filterByName(teamPaws, "teamPaws");
  const filteredTeamSocial = filterByName(teamSocial, "teamSocial");

  return (
    <Box sx={{ maxWidth: "1200px", margin: "0 auto", p: { xs: 2, md: 2 } }}>
      <Box display="flex" alignItems="center" mb={4}>
        <Typography variant="h4" fontWeight="bold" sx={{ color: "#009fd9" }}>
          {selectionMode ? "Members" : "New Join Members"} - {teamPaws.length + teamSocial.length}{!selectionMode && ` (${getDisplayTitle()})`}
        </Typography>
      </Box>

      {selectionMode && (
        <Typography sx={{ mb: 3 }} color="text.secondary">
          Select at least two members from either list, then click Preview Email.
        </Typography>
      )}
      {/* Team Paws Section */}
      <Box mb={4}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between", gap: 2, mb: 3, pb: 1, borderBottom: "2px solid #f0f0f0" }}>
        <Typography
          variant="h5"
          sx={{
            color: "#005b7d",
            fontWeight: "bold",
          }}
        >
          Pup Parents - {teamPaws.length} Member
          {teamPaws.length !== 1 ? "s" : ""}
        </Typography>
          {selectionMode && (
            <TextField
              label="Search Pup Parents by name"
              type="search"
              size="small"
              value={nameSearch.teamPaws}
              onChange={(event) => setNameSearch((previous) => ({ ...previous, teamPaws: event.target.value }))}
              sx={{ width: { xs: "100%", sm: 300 }, flexShrink: 0 }}
            />
          )}
        </Box>
        {selectionMode && (
          <Button
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            aria-expanded={expandedTeams.teamPaws}
            aria-controls="teamPaws-members"
            aria-label={`${expandedTeams.teamPaws ? "Minimize" : "Expand"} Pup Parents`}
            onClick={() => setExpandedTeams((previous) => ({ ...previous, teamPaws: !previous.teamPaws }))}
          >
            {expandedTeams.teamPaws ? "Minimize" : "Expand"}
          </Button>
        )}
        <Box id="teamPaws-members" hidden={selectionMode && !expandedTeams.teamPaws}>

        <Grid container spacing={3} alignItems="flex-start">
          {filteredTeamPaws.length > 0 ? (
            filteredTeamPaws.map((member) => (
              <Grid item xs={12} sm={6} md={4} key={member.id}>
                <MemberCard
                  member={member}
                  teamType="teamPaws"
                  selectable={selectionMode}
                  selected={selectedMembers.some((selected) => selected.id === member.id)}
                  disabled={isSendingEmail}
                  onSelect={() => toggleMember(member, "teamPaws")}
                />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography sx={{ pl: 2, color: "text.secondary" }}>
                {nameSearch.teamPaws.trim() ? "No members match this name." : "No members for this period."}
              </Typography>
            </Grid>
          )}
        </Grid>
        </Box>
      </Box>

      <Divider sx={{ my: 5 }} />

      {/* Pup Pals Section */}
      <Box mb={4}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between", gap: 2, mb: 3, pb: 1, borderBottom: "2px solid #f0f0f0" }}>
        <Typography
          variant="h5"
          sx={{
            color: "#005b7d",
            fontWeight: "bold",
          }}
        >
          Pup Pals - {teamSocial.length} Member
          {teamSocial.length !== 1 ? "s" : ""}
        </Typography>
          {selectionMode && (
            <TextField
              label="Search Pup Pals by name"
              type="search"
              size="small"
              value={nameSearch.teamSocial}
              onChange={(event) => setNameSearch((previous) => ({ ...previous, teamSocial: event.target.value }))}
              sx={{ width: { xs: "100%", sm: 300 }, flexShrink: 0 }}
            />
          )}
        </Box>
        {selectionMode && (
          <Button
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            aria-expanded={expandedTeams.teamSocial}
            aria-controls="teamSocial-members"
            aria-label={`${expandedTeams.teamSocial ? "Minimize" : "Expand"} Pup Pals`}
            onClick={() => setExpandedTeams((previous) => ({ ...previous, teamSocial: !previous.teamSocial }))}
          >
            {expandedTeams.teamSocial ? "Minimize" : "Expand"}
          </Button>
        )}
        <Box id="teamSocial-members" hidden={selectionMode && !expandedTeams.teamSocial}>

        <Grid container spacing={3} alignItems="flex-start">
          {filteredTeamSocial.length > 0 ? (
            filteredTeamSocial.map((member) => (
              <Grid item xs={12} sm={6} md={4} key={member.id}>
                <MemberCard
                  member={member}
                  teamType="teamSocial"
                  selectable={selectionMode}
                  selected={selectedMembers.some((selected) => selected.id === member.id)}
                  disabled={isSendingEmail}
                  onSelect={() => toggleMember(member, "teamSocial")}
                />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography sx={{ pl: 2, color: "text.secondary" }}>
                {nameSearch.teamSocial.trim() ? "No members match this name." : "No members for this period."}
              </Typography>
            </Grid>
          )}
        </Grid>
        </Box>
      </Box>
      {selectionMode && (
        <Box sx={{ position: "sticky", bottom: 0, bgcolor: "background.paper", p: 2, borderTop: "1px solid #e0e0e0", zIndex: 1 }}>
          <Typography sx={{ mb: 1 }}>
            {selectedMembers.length} selected{selectedMembers.length > 0 && `: ${selectedMembers.map((member) => member.user_name || member.user_email || member.id).join(", ")}`}
          </Typography>
          {emailStatus.msg && !isEmailPreviewOpen && <Alert severity={emailStatus.type} sx={{ mb: 1 }}>{emailStatus.msg}</Alert>}
          <Button variant="contained" disabled={selectedMembers.length < 2 || isSendingEmail} onClick={() => {
            setEmailStatus({ type: "", msg: "" });
            setPreviewPage(0);
            setIsEmailPreviewOpen(true);
          }}>
            Preview Email
          </Button>
        </Box>
      )}
      <Dialog
        open={isEmailPreviewOpen}
        onClose={() => !isSendingEmail && setIsEmailPreviewOpen(false)}
        fullWidth
        maxWidth="lg"
        aria-labelledby="email-preview-title"
      >
        <DialogTitle id="email-preview-title">Preview Emails</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {selectedMembers.slice(previewPage * 2, previewPage * 2 + 2).map((recipient) => {
              const suggestedMembers = selectedMembers.filter((member) => member.id !== recipient.id);
              return (
                <Grid item xs={12} md={6} key={recipient.id}>
                  <Box sx={{ mb: 2, overflowWrap: "anywhere" }}>
                    <Typography fontWeight="bold">To: {recipient.user_name}</Typography>
                    <Typography variant="body2" color="text.secondary">{recipient.user_email}</Typography>
                    <Typography variant="body2">Introducing: {suggestedMembers.map((member) => member.user_name).join(", ")}</Typography>
                  </Box>
                  <Box sx={{ border: "1px solid #e0e0e0", p: 2, bgcolor: "#fff", color: "#000", textAlign: "center" }}>
                    <Box sx={{ background: "linear-gradient(100deg, #60c3b4, #32b9df, #28629e)", p: 3, display: "flex", justifyContent: "space-around", alignItems: "center", gap: 2 }}>
                      <Typography sx={{ color: "white", fontSize: { xs: 28, sm: 36 }, fontWeight: 700 }}>Social Paws</Typography>
                      <Box component="img" src={logo} alt="Social Paws logo" sx={{ width: 72, height: 72, objectFit: "contain", borderRadius: "50%", bgcolor: "white" }} />
                    </Box>
                    <Typography sx={{ mt: 3 }}>We spotted a fellow Social Paws member we think you should meet! 🐶</Typography>
                    <Typography sx={{ mt: 3 }}>Sniff out their profile, toss a treat, and maybe even offer a walk</Typography>
                    <Typography sx={{ mt: 3 }}>All good introductions start with a wag 💙</Typography>
                    {suggestedMembers.map((suggestedMember) => (
                    <Button
                      key={suggestedMember.id}
                      component="a"
                      href={`https://webapp.socialpawsapp.com/profile-details?profileId=${encodeURIComponent(suggestedMember.id)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="contained"
                      sx={{ mt: 5, mb: 5, px: 2.5, py: 1.5, bgcolor: "#309ece", color: "white", fontWeight: 600, textTransform: "none" }}
                    >
                      Sniff out the Connection! 🐾{suggestedMembers.length > 1 && ` — ${suggestedMember.user_name}`}
                    </Button>
                    ))}
                    <Typography>Social Paws</Typography>
                    <Typography sx={{ fontStyle: "italic", mb: 1 }}>Where everyone gets to love a dog</Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
          {emailStatus.msg && <Alert severity={emailStatus.type} sx={{ mt: 2 }}>{emailStatus.msg}</Alert>}
        </DialogContent>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, px: 3, py: 1 }}>
          <Button aria-label="Previous email previews" disabled={previewPage === 0} onClick={() => setPreviewPage((page) => page - 1)}>← Previous</Button>
          <Typography variant="body2" aria-live="polite">
            {previewPage * 2 + 1}–{Math.min(previewPage * 2 + 2, selectedMembers.length)} of {selectedMembers.length}
          </Typography>
          <Button aria-label="Next email previews" disabled={(previewPage + 1) * 2 >= selectedMembers.length} onClick={() => setPreviewPage((page) => page + 1)}>Next →</Button>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button disabled={isSendingEmail} onClick={() => setIsEmailPreviewOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={selectedMembers.length < 2 || isSendingEmail} onClick={handleSendEmail}>
            {isSendingEmail ? "Sending..." : "Send Email"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewJoinMembers;
